import { useEffect, useState } from 'react'
import {
  Box, Button, Input, Text, VStack, HStack, IconButton,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useDisclosure, FormControl, FormLabel, Accordion, AccordionItem,
  AccordionButton, AccordionPanel, AccordionIcon, Spinner
} from '@chakra-ui/react'
import { FaPlus, FaEdit, FaTrash, FaUpload, FaLink, FaExternalLinkAlt } from 'react-icons/fa'
import { containerWidth } from 'styles'
import useUIContext from 'hooks/ui-context'
import { API_URL } from 'constants'
import {
  getDocumentCategories,
  createDocumentCategory,
  updateDocumentCategory,
  deleteDocumentCategory,
  getDocumentsByCategory,
  uploadDocument,
  createLink,
  updateDocument,
  deleteDocument
} from 'services/documents'

const DocumentsManage = () => {
  const [categories, setCategories] = useState([])
  const [documents, setDocuments] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { setBreadcrumbs, addNotification } = useUIContext()

  // Category modal
  const { isOpen: isCategoryModalOpen, onOpen: onCategoryModalOpen, onClose: onCategoryModalClose } = useDisclosure()
  const [editingCategory, setEditingCategory] = useState(null)
  const [categoryName, setCategoryName] = useState('')

  // Document modal
  const { isOpen: isDocModalOpen, onOpen: onDocModalOpen, onClose: onDocModalClose } = useDisclosure()
  const [editingDoc, setEditingDoc] = useState(null)
  const [docName, setDocName] = useState('')
  const [docFile, setDocFile] = useState(null)
  const [selectedCategoryId, setSelectedCategoryId] = useState(null)

  // Link modal
  const { isOpen: isLinkModalOpen, onOpen: onLinkModalOpen, onClose: onLinkModalClose } = useDisclosure()
  const [linkName, setLinkName] = useState('')
  const [linkUrl, setLinkUrl] = useState('')

  // Delete confirmation modal
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure()
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteType, setDeleteType] = useState(null)

  useEffect(() => {
    setBreadcrumbs([
      { path: '/admin/dashboard', label: 'Dashboard' },
      { path: '/admin/dashboard/documents-manage', label: 'Manage Documents' }
    ])
    loadCategories()
  }, [])

  const loadCategories = async () => {
    setLoading(true)
    try {
      const res = await getDocumentCategories()
      if (res.data) {
        setCategories(res.data)
        const docsMap = {}
        for (const cat of res.data) {
          const docsRes = await getDocumentsByCategory(cat._id)
          docsMap[cat._id] = docsRes.data || []
        }
        setDocuments(docsMap)
      }
    } catch (err) {
      addNotification({ status: 'error', content: 'Failed to load documents' })
    }
    setLoading(false)
  }

  // Category handlers
  const handleOpenCategoryModal = (category = null) => {
    setEditingCategory(category)
    setCategoryName(category?.name || '')
    onCategoryModalOpen()
  }

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      addNotification({ status: 'error', content: 'Category name is required' })
      return
    }
    setSaving(true)
    try {
      if (editingCategory) {
        await updateDocumentCategory(editingCategory._id, categoryName)
        addNotification({ status: 'success', content: 'Category updated' })
      } else {
        await createDocumentCategory(categoryName)
        addNotification({ status: 'success', content: 'Category created' })
      }
      onCategoryModalClose()
      setCategoryName('')
      setEditingCategory(null)
      loadCategories()
    } catch (err) {
      addNotification({ status: 'error', content: 'Failed to save category' })
    }
    setSaving(false)
  }

  const handleConfirmDeleteCategory = (category) => {
    setDeleteTarget(category)
    setDeleteType('category')
    onDeleteModalOpen()
  }

  const handleDeleteCategory = async () => {
    setSaving(true)
    try {
      await deleteDocumentCategory(deleteTarget._id)
      addNotification({ status: 'success', content: 'Category deleted' })
      onDeleteModalClose()
      setDeleteTarget(null)
      loadCategories()
    } catch (err) {
      addNotification({ status: 'error', content: 'Failed to delete category' })
    }
    setSaving(false)
  }

  // Document handlers
  const handleOpenDocModal = (categoryId, doc = null) => {
    setSelectedCategoryId(categoryId)
    setEditingDoc(doc)
    setDocName(doc?.name || '')
    setDocFile(null)
    onDocModalOpen()
  }

  const handleSaveDocument = async () => {
    if (!docName.trim()) {
      addNotification({ status: 'error', content: 'Document name is required' })
      return
    }
    if (!editingDoc && !docFile) {
      addNotification({ status: 'error', content: 'Please select a file' })
      return
    }
    setSaving(true)
    try {
      if (editingDoc) {
        await updateDocument(editingDoc._id, { name: docName })
        addNotification({ status: 'success', content: 'Document updated' })
      } else {
        const formData = new FormData()
        formData.append('name', docName)
        formData.append('file', docFile)
        formData.append('category', selectedCategoryId)
        await uploadDocument(formData)
        addNotification({ status: 'success', content: 'Document uploaded' })
      }
      onDocModalClose()
      setDocName('')
      setDocFile(null)
      setEditingDoc(null)
      loadCategories()
    } catch (err) {
      addNotification({ status: 'error', content: 'Failed to save document' })
    }
    setSaving(false)
  }

  const handleConfirmDeleteDocument = (doc) => {
    setDeleteTarget(doc)
    setDeleteType('document')
    onDeleteModalOpen()
  }

  // Link handlers
  const handleOpenLinkModal = (categoryId) => {
    setSelectedCategoryId(categoryId)
    setLinkName('')
    setLinkUrl('')
    onLinkModalOpen()
  }

  const handleSaveLink = async () => {
    if (!linkName.trim()) {
      addNotification({ status: 'error', content: 'Link name is required' })
      return
    }
    if (!linkUrl.trim()) {
      addNotification({ status: 'error', content: 'URL is required' })
      return
    }
    setSaving(true)
    try {
      await createLink({
        name: linkName,
        external_url: linkUrl,
        category: selectedCategoryId
      })
      addNotification({ status: 'success', content: 'Link added' })
      onLinkModalClose()
      setLinkName('')
      setLinkUrl('')
      loadCategories()
    } catch (err) {
      addNotification({ status: 'error', content: 'Failed to add link' })
    }
    setSaving(false)
  }

  const handleDeleteDocument = async () => {
    setSaving(true)
    try {
      await deleteDocument(deleteTarget._id)
      addNotification({ status: 'success', content: 'Document deleted' })
      onDeleteModalClose()
      setDeleteTarget(null)
      loadCategories()
    } catch (err) {
      addNotification({ status: 'error', content: 'Failed to delete document' })
    }
    setSaving(false)
  }

  if (loading) {
    return (
      <Box width={containerWidth} mx="auto" py={10} textAlign="center">
        <Spinner size="xl" />
      </Box>
    )
  }

  return (
    <Box width={containerWidth} mx="auto" pb={10}>
      <HStack justifyContent="space-between" mb={6}>
        <Text fontSize="2xl" fontWeight="bold">Manage Documents</Text>
        <Button
          leftIcon={<FaPlus />}
          colorScheme="green"
          onClick={() => handleOpenCategoryModal()}
        >
          Add Category
        </Button>
      </HStack>

      {categories.length === 0 ? (
        <Text color="gray.500">No categories yet. Create one to get started.</Text>
      ) : (
        <Accordion allowMultiple defaultIndex={categories.map((_, i) => i)}>
          {categories.map(category => (
            <AccordionItem key={category._id} borderWidth={1} borderRadius="md" mb={3}>
              <AccordionButton py={3}>
                <Box flex="1" textAlign="left" fontWeight="bold" fontSize="lg">
                  {category.name}
                  <Text as="span" fontWeight="normal" fontSize="sm" color="gray.500" ml={2}>
                    ({(documents[category._id] || []).length} documents)
                  </Text>
                </Box>
                <HStack spacing={2} onClick={e => e.stopPropagation()}>
                  <IconButton
                    size="sm"
                    icon={<FaUpload />}
                    aria-label="Upload document"
                    title="Upload document"
                    onClick={() => handleOpenDocModal(category._id)}
                  />
                  <IconButton
                    size="sm"
                    icon={<FaLink />}
                    aria-label="Add link"
                    title="Add external link"
                    onClick={() => handleOpenLinkModal(category._id)}
                  />
                  <IconButton
                    size="sm"
                    icon={<FaEdit />}
                    aria-label="Edit category"
                    title="Edit category"
                    onClick={() => handleOpenCategoryModal(category)}
                  />
                  <IconButton
                    size="sm"
                    icon={<FaTrash />}
                    colorScheme="red"
                    variant="outline"
                    aria-label="Delete category"
                    title="Delete category"
                    onClick={() => handleConfirmDeleteCategory(category)}
                  />
                </HStack>
                <AccordionIcon ml={2} />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <VStack align="stretch" spacing={2}>
                  {(documents[category._id] || []).map(doc => (
                    <HStack key={doc._id} p={3} bg="gray.50" borderRadius="md">
                      {doc.external_url && <FaExternalLinkAlt size={12} color="gray" />}
                      <Text flex="1" fontWeight="medium">{doc.name}</Text>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (doc.external_url) {
                            window.open(doc.external_url, '_blank')
                          } else {
                            window.open(`${API_URL}/s3/${encodeURIComponent(doc.file_key)}`, '_blank')
                          }
                        }}
                      >
                        {doc.external_url ? 'Open Link' : 'View'}
                      </Button>
                      <IconButton
                        size="sm"
                        icon={<FaEdit />}
                        aria-label="Edit document"
                        title="Edit document"
                        onClick={() => handleOpenDocModal(category._id, doc)}
                      />
                      <IconButton
                        size="sm"
                        icon={<FaTrash />}
                        colorScheme="red"
                        variant="outline"
                        aria-label="Delete document"
                        title="Delete document"
                        onClick={() => handleConfirmDeleteDocument(doc)}
                      />
                    </HStack>
                  ))}
                  {(documents[category._id] || []).length === 0 && (
                    <Text color="gray.500" fontStyle="italic" py={2}>
                      No documents in this category. Click the upload button to add one.
                    </Text>
                  )}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Category Modal */}
      <Modal isOpen={isCategoryModalOpen} onClose={onCategoryModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingCategory ? 'Edit Category' : 'Add Category'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Category Name</FormLabel>
              <Input
                value={categoryName}
                onChange={e => setCategoryName(e.target.value)}
                placeholder="Enter category name"
                autoFocus
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCategoryModalClose}>
              Cancel
            </Button>
            <Button colorScheme="green" onClick={handleSaveCategory} isLoading={saving}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Document Modal */}
      <Modal isOpen={isDocModalOpen} onClose={onDocModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingDoc ? 'Edit Document' : 'Upload Document'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Document Name</FormLabel>
              <Input
                value={docName}
                onChange={e => setDocName(e.target.value)}
                placeholder="Enter document name"
                autoFocus
              />
            </FormControl>
            {!editingDoc && (
              <FormControl>
                <FormLabel>File</FormLabel>
                <Input
                  type="file"
                  onChange={e => setDocFile(e.target.files[0])}
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  p={1}
                />
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDocModalClose}>
              Cancel
            </Button>
            <Button colorScheme="green" onClick={handleSaveDocument} isLoading={saving}>
              {editingDoc ? 'Save' : 'Upload'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Delete {deleteType === 'category' ? 'Category' : 'Document'}?
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {deleteType === 'category' ? (
              <Text>
                This will delete the category "{deleteTarget?.name}" and all documents within it.
                This action cannot be undone.
              </Text>
            ) : (
              <Text>
                This will delete the document "{deleteTarget?.name}".
                This action cannot be undone.
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={deleteType === 'category' ? handleDeleteCategory : handleDeleteDocument}
              isLoading={saving}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Link Modal */}
      <Modal isOpen={isLinkModalOpen} onClose={onLinkModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add External Link</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Link Name</FormLabel>
              <Input
                value={linkName}
                onChange={e => setLinkName(e.target.value)}
                placeholder="Enter link name (e.g., Work Permit Application)"
                autoFocus
              />
            </FormControl>
            <FormControl>
              <FormLabel>URL</FormLabel>
              <Input
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onLinkModalClose}>
              Cancel
            </Button>
            <Button colorScheme="green" onClick={handleSaveLink} isLoading={saving}>
              Add Link
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default DocumentsManage
